import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import LoaderBox from "../HomePage/LoaderBox";

const AdminSettingsStats = ({ onBack, turnir }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const tournamentId = turnir?.id;

  useEffect(() => {
    const calculateLiveStats = async () => {
      if (!tournamentId) {
        console.log("DEBUG STATS: Nema tournamentId-a!");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Dohvaćamo utakmice i povezane timove
        const { data: matches, error } = await supabase
          .from("turniri_utakmice")
          .select(
            `
            *,
            team_a:team_a_id(name),
            team_b:team_b_id(name)
          `,
          )
          .eq("tournament_id", tournamentId);

        if (error) {
          console.error("DEBUG STATS Supabase error:", error);
          throw error;
        }

        console.log("DEBUG STATS: Dohvaćene utakmice:", matches);

        if (matches) {
          let stvarnoOdigrano = 0;
          let ukupnoGolova = 0;
          let najvecaRazlika = -1;
          let najvećePobjedeLista = [];

          const timoviStats = {};
          const unikatniTimovi = new Set();

          matches.forEach((match) => {
            const timA = match.team_a?.name || "Tim A";
            const timB = match.team_b?.name || "Tim B";

            if (match.team_a_id) unikatniTimovi.add(match.team_a_id);
            if (match.team_b_id) unikatniTimovi.add(match.team_b_id);

            if (!timoviStats[timA]) timoviStats[timA] = { zabio: 0, primio: 0 };
            if (!timoviStats[timB]) timoviStats[timB] = { zabio: 0, primio: 0 };

            if (match.is_completed) {
              stvarnoOdigrano += 1;
              const sA = match.scorea || 0;
              const sB = match.scoreb || 0;
              ukupnoGolova += sA + sB;

              timoviStats[timA].zabio += sA;
              timoviStats[timA].primio += sB;

              timoviStats[timB].zabio += sB;
              timoviStats[timB].primio += sA;

              const razlika = Math.abs(sA - sB);
              if (razlika >= najvecaRazlika) {
                const tekstMeca = `${timA} vs ${timB} (${sA}:${sB})`;

                if (razlika > najvecaRazlika) {
                  najvecaRazlika = razlika;
                  najvećePobjedeLista = [tekstMeca];
                } else {
                  najvećePobjedeLista.push(tekstMeca);
                }
              }
            }
          });

          let najvisePrimljenih = { tim: "-", golovi: -1 };
          let najboljiNapad = { tim: "-", golovi: 0 };
          let najboljaObrana = { tim: "-", golovi: Infinity };

          Object.keys(timoviStats).forEach((tim) => {
            if (timoviStats[tim].primio > najvisePrimljenih.golovi) {
              najvisePrimljenih = { tim: tim, golovi: timoviStats[tim].primio };
            }
            if (timoviStats[tim].zabio > najboljiNapad.golovi) {
              najboljiNapad = { tim: tim, golovi: timoviStats[tim].zabio };
            }
            if (timoviStats[tim].primio < najboljaObrana.golovi) {
              najboljaObrana = { tim: tim, golovi: timoviStats[tim].primio };
            }
          });

          if (najboljaObrana.golovi === Infinity) najboljaObrana.golovi = 0;
          if (najvisePrimljenih.golovi === -1) najvisePrimljenih.golovi = 0;

          const prosjek =
            stvarnoOdigrano > 0
              ? (ukupnoGolova / stvarnoOdigrano).toFixed(2)
              : 0;

          setStats({
            brojEkipa: unikatniTimovi.size,
            stvarnoOdigrano,
            ukupnoGolova,
            prosjek,
            najvećePobjedeLista: najvećePobjedeLista.slice(0, 5),
            najboljiNapad,
            najboljaObrana,
            najvisePrimljenih,
          });
        }
      } catch (err) {
        console.error("Greška u statistikama:", err.message);
      } finally {
        setLoading(false);
      }
    };

    calculateLiveStats();
  }, [tournamentId]);

  if (loading) return <LoaderBox text="Generiram izvještaj..." />;
  if (!stats)
    return (
      <p className="error-text" style={{ padding: "20px", color: "#fff" }}>
        Nema podataka za prikaz statistike.
      </p>
    );

  return (
    <div className="admin-stats-dashboard">
      <button className="back-btn" onClick={onBack}>
        <i className="fi fi-rs-arrow-left"></i> Natrag
      </button>
      <div className="dashboard-header">
        <h3>STATISTIKA TURNIRA</h3>
        <p className="subtitle"></p>
      </div>
      <div className="stats-list">
        <div className="stat-card">
          <div className="card-info">
            <span className="card-label">
              Broj ekipa<i className="fi fi-rs-employees"></i>
            </span>
            <span className="card-desc">Ukupan broj ekipa</span>
          </div>
          <div className="card-badge">{stats.brojEkipa}</div>
        </div>
        <div className="stat-card">
          <div className="card-info">
            <span className="card-label">
              Odigrane utakmice<i className="fi fi-rs-court-sport"></i>
            </span>
            <span className="card-desc">
              Ukupan broj odigranih utakmica na turniru
            </span>
          </div>
          <div className="card-badge">{stats.stvarnoOdigrano}</div>
        </div>

        <div className="stat-card">
          <div className="card-info">
            <span className="card-label">
              Postignuto golova <i className="fi fi-rs-football"></i>
            </span>
            <span className="card-desc">Zbroj svih zabijenih golova</span>
          </div>
          <div className="card-badge">{stats.ukupnoGolova}</div>
        </div>

        <div className="stat-card">
          <div className="card-info">
            <span className="card-label">Prosjek po utakmici</span>
            <span className="card-desc">
              Prosjek zabijenih golova po utakmici
            </span>
          </div>
          <div className="card-badge accent">{stats.prosjek}</div>
        </div>

        <div className="section-divider">EKIPNI REKORDI</div>

        <div className="stat-card leader-card napad">
          <div className="card-info">
            <span className="card-label">Najviše zabijenih golova</span>
            <span className="card-team-name">{stats.najboljiNapad.tim}</span>
          </div>
          <div className="card-badge-large">
            {stats.najboljiNapad.golovi} <span>G</span>
          </div>
        </div>

        <div className="stat-card leader-card obrana">
          <div className="card-info">
            <span className="card-label">Najmanje primljenih golova</span>
            <span className="card-team-name">{stats.najboljaObrana.tim}</span>
          </div>
          <div className="card-badge-large">
            {stats.najboljaObrana.golovi} <span>P</span>
          </div>
        </div>

        <div className="stat-card leader-card najgora-obrana">
          <div className="card-info">
            <span className="card-label">Najviše primljenih golova</span>
            <span className="card-team-name">
              {stats.najvisePrimljenih.tim}
            </span>
          </div>
          <div className="card-badge-large">
            {stats.najvisePrimljenih.golovi} <span>P</span>
          </div>
        </div>

        <div className="section-divider">NAJVEĆE POBJEDE</div>

        <div className="records-vertical">
          {stats.najvećePobjedeLista.length > 0 ? (
            stats.najvećePobjedeLista.map((meca, index) => (
              <div key={index} className="record-row-item">
                <div className="icon-holder">
                  <i className="fi fi-rs-trophy"></i>
                </div>
                <span className="record-text">{meca}</span>
              </div>
            ))
          ) : (
            <p
              className="no-results"
              style={{ padding: "10px", color: "#888" }}
            >
              Nema zabilježenih pobjeda
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsStats;
